/**
 * PublishPanel - Deploy voice apps to production
 *
 * A slide-out panel for publishing voice apps:
 * - Live preview in phone frame
 * - Hosting configuration (subdomain, custom domain)
 * - Publish status and share options
 *
 * This panel collects all connected screens and generates
 * an AppDefinition JSON for the voice runtime player.
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import type { Node, Edge } from '@xyflow/react';
import type {
  AppSettings,
  AppScreenNodeData,
  AppDefinition,
  AppPublishStatus,
  AppHostingConfig,
  PublishedScreen,
  NavigationEdgeData,
} from '@deep-agents/shared-types';
import { createDefaultAppSettings } from '@deep-agents/shared-types';
import { findConnectedScreens, findHomeScreen } from './AppSettingsModal';

interface PublishPanelProps {
  /** Whether the panel is open */
  isOpen: boolean;
  /** Called when panel should close */
  onClose: () => void;
  /** The node ID that opened the panel */
  sourceNodeId: string;
  /** All nodes in the board */
  nodes: Node[];
  /** All edges in the board */
  edges: Edge[];
  /** Board ID for unique app identification */
  boardId: string;
}

export function PublishPanel({
  isOpen,
  onClose,
  sourceNodeId,
  nodes,
  edges,
  boardId,
}: PublishPanelProps) {
  // Local state for hosting config
  const [subdomain, setSubdomain] = useState('');
  const [customDomain, setCustomDomain] = useState('');
  const [allowEmbedding, setAllowEmbedding] = useState(true);
  const [isPublishing, setIsPublishing] = useState(false);
  const [isLoadingStatus, setIsLoadingStatus] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Agent ID returned by the publish endpoint (created by ElevenLabs)
  // We store this so the Preview button can include it in the player data
  const [publishedAgentId, setPublishedAgentId] = useState<string | null>(null);

  // Publish status from API
  const [publishStatus, setPublishStatus] = useState<AppPublishStatus>({
    isPublished: false,
  });

  // Load existing publish status when panel opens
  useEffect(() => {
    if (!isOpen || !boardId) return;

    async function loadPublishStatus() {
      setIsLoadingStatus(true);
      setError(null);

      try {
        const response = await fetch(`/api/boards/${boardId}/app`, {
          credentials: 'include',
        });

        if (!response.ok) {
          throw new Error('Failed to load publish status');
        }

        const data = await response.json();

        if (data.success && data.data.isPublished) {
          setPublishStatus({
            isPublished: true,
            subdomain: data.data.subdomain,
            customDomain: data.data.customDomain,
            liveUrl: data.data.liveUrl,
            version: data.data.version,
            lastPublishedAt: data.data.updatedAt,
          });
          // Pre-fill form with existing values
          setSubdomain(data.data.subdomain || '');
          setCustomDomain(data.data.customDomain || '');
          setAllowEmbedding(data.data.allowEmbedding !== false);
        } else {
          setPublishStatus({ isPublished: false });
        }
      } catch (err) {
        console.error('[PublishPanel] Failed to load status:', err);
        // Don't show error for loading status - just start fresh
      } finally {
        setIsLoadingStatus(false);
      }
    }

    loadPublishStatus();
  }, [isOpen, boardId]);

  // Find connected screens for this app
  const connectedScreens = useMemo(() => {
    return findConnectedScreens(sourceNodeId, nodes, edges);
  }, [sourceNodeId, nodes, edges]);

  // Get app settings from home screen
  const appSettings = useMemo((): AppSettings => {
    const homeNode = findHomeScreen(sourceNodeId, nodes, edges);
    if (homeNode) {
      const homeData = homeNode.data as AppScreenNodeData;
      if (homeData.appSettings) {
        return homeData.appSettings;
      }
    }
    // Find any screen that might have settings
    const sourceNode = nodes.find((n) => n.id === sourceNodeId);
    if (sourceNode) {
      const data = sourceNode.data as AppScreenNodeData;
      if (data.appSettings) {
        return data.appSettings;
      }
    }
    return createDefaultAppSettings();
  }, [sourceNodeId, nodes, edges]);

  // Generate app definition for publishing
  const generateAppDefinition = useCallback((): AppDefinition => {
    const appScreenNodes = nodes.filter((n) => n.type === 'appScreen');

    // Include ALL appScreen nodes (not just connected ones)
    // This makes it easier to build and test apps
    console.log('[Publish] ========== GENERATING APP DEFINITION ==========');
    console.log('[Publish] Total nodes passed to PublishPanel:', nodes.length);
    console.log('[Publish] AppScreen nodes found:', appScreenNodes.length);

    // Debug: log ALL nodes to see what's in them
    nodes.forEach((n, i) => {
      console.log(`[Publish] Node ${i}: type=${n.type}, id=${n.id}, data=`, n.data);
    });

    const screens: PublishedScreen[] = appScreenNodes
      .map((node) => {
        const data = node.data as AppScreenNodeData;
        console.log('[Publish] Processing screen:', data.title);
        console.log('[Publish] - widgets array:', data.widgets);
        console.log('[Publish] - widgets count:', data.widgets?.length || 0);

        // Find navigation edges from this screen
        // Include ANY edge between two appScreen nodes as navigation,
        // even if edge.data.type isn't explicitly set (e.g. template edges).
        const appScreenIds = new Set(appScreenNodes.map((n) => n.id));
        const navEdges = edges.filter(
          (e) => e.source === node.id && appScreenIds.has(e.target)
        );

        return {
          id: node.id,
          name: data.title || 'Untitled Screen',
          isHome: data.isHomeScreen || false,
          widgets: data.widgets || [],
          voice: data.voice,
          navigatesTo: navEdges.map((e) => {
            const edgeData = e.data as unknown as NavigationEdgeData | undefined;
            return {
              targetScreenId: e.target,
              triggerPhrase: edgeData?.triggerPhrase,
              sourceWidgetId: edgeData?.sourceWidgetId,
              transition: edgeData?.transition,
            };
          }),
        };
      });

    // Find home screen
    const homeScreen = screens.find((s) => s.isHome) || screens[0];

    return {
      id: `app-${boardId}-${Date.now()}`,
      version: (publishStatus.version || 0) + 1,
      publishedAt: new Date().toISOString(),
      settings: appSettings,
      screens,
      homeScreenId: homeScreen?.id || '',
    };
  }, [nodes, edges, appSettings, boardId, publishStatus.version]);

  // Get screens that will be published (for confirmation modal)
  const screensToPublish = useMemo(() => {
    const appScreenNodes = nodes.filter((n) => n.type === 'appScreen');
    return appScreenNodes.map((node) => {
      const data = node.data as AppScreenNodeData;
      return {
        id: node.id,
        name: data.title || 'Untitled Screen',
        isHome: data.isHomeScreen || false,
        widgetCount: data.widgets?.length || 0,
      };
    });
  }, [nodes]);

  // Show confirmation modal (validates first)
  const handleShowConfirmation = useCallback(() => {
    // Validate subdomain
    const trimmedSubdomain = subdomain.trim().toLowerCase();
    if (!trimmedSubdomain) {
      setError('Please enter a subdomain');
      return;
    }

    if (trimmedSubdomain.length < 3) {
      setError('Subdomain must be at least 3 characters');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(trimmedSubdomain)) {
      setError('Subdomain can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    if (trimmedSubdomain.startsWith('-') || trimmedSubdomain.endsWith('-')) {
      setError('Subdomain cannot start or end with a hyphen');
      return;
    }

    // Show confirmation
    setError(null);
    setShowConfirmation(true);
  }, [subdomain]);

  // Handle actual publish action (called from confirmation modal)
  const handlePublish = useCallback(async () => {
    setShowConfirmation(false);

    // Validate subdomain (already validated, but just in case)
    const trimmedSubdomain = subdomain.trim().toLowerCase();
    if (!trimmedSubdomain) {
      setError('Please enter a subdomain');
      return;
    }

    if (trimmedSubdomain.length < 3) {
      setError('Subdomain must be at least 3 characters');
      return;
    }

    if (!/^[a-z0-9-]+$/.test(trimmedSubdomain)) {
      setError('Subdomain can only contain lowercase letters, numbers, and hyphens');
      return;
    }

    if (trimmedSubdomain.startsWith('-') || trimmedSubdomain.endsWith('-')) {
      setError('Subdomain cannot start or end with a hyphen');
      return;
    }

    setIsPublishing(true);
    setError(null);

    try {
      const appDefinition = generateAppDefinition();

      console.log('[PublishPanel] Publishing app:', appDefinition.settings.name);

      const response = await fetch(`/api/boards/${boardId}/publish-app`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          appDefinition,
          hosting: {
            subdomain: trimmedSubdomain,
            customDomain: customDomain || undefined,
            allowEmbedding,
          },
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        // Handle specific error cases
        if (response.status === 409) {
          setError('This subdomain is already taken. Please choose another.');
        } else if (response.status === 400) {
          setError(data.error || 'Invalid subdomain format');
        } else {
          setError(data.error || 'Failed to publish app');
        }
        return;
      }

      // Update status on success
      setPublishStatus({
        isPublished: true,
        subdomain: data.data.subdomain,
        customDomain: customDomain || undefined,
        lastPublishedAt: new Date().toISOString(),
        liveUrl: data.data.liveUrl,
        version: data.data.version,
      });

      // Store the agentId so Preview can include it
      if (data.data.agentId) {
        setPublishedAgentId(data.data.agentId);
        console.log('[PublishPanel] Voice agent ID:', data.data.agentId);
      }

      console.log('[PublishPanel] Published successfully:', data.data.liveUrl);
    } catch (err) {
      console.error('[PublishPanel] Publish failed:', err);
      setError('Failed to publish app. Please try again.');
    } finally {
      setIsPublishing(false);
    }
  }, [subdomain, customDomain, allowEmbedding, generateAppDefinition, boardId]);

  // Handle unpublish — calls the API to soft-delete, then clears local state
  const handleUnpublish = useCallback(async () => {
    try {
      const response = await fetch(`/api/boards/${boardId}/unpublish-app`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        const data = await response.json();
        setError(data.error || 'Failed to unpublish app');
        return;
      }

      // Clear local publish state
      setPublishStatus({ isPublished: false });
      setSubdomain('');
      setCustomDomain('');
    } catch (err) {
      console.error('[PublishPanel] Unpublish failed:', err);
      setError('Failed to unpublish app. Please try again.');
    }
  }, [boardId]);

  // Handle copy link
  const handleCopyLink = useCallback(() => {
    if (publishStatus.liveUrl) {
      navigator.clipboard.writeText(publishStatus.liveUrl);
      alert('Link copied to clipboard!');
    }
  }, [publishStatus.liveUrl]);

  // Handle preview in player - opens player with app data encoded in URL
  const handlePreview = useCallback(() => {
    const appDefinition = generateAppDefinition();

    // If we have a published agentId, inject it into the app definition
    // so the player can connect to the ElevenLabs voice agent.
    // Without this, Preview would have no agentId and voice wouldn't work.
    if (publishedAgentId) {
      if (!appDefinition.settings.agent) {
        appDefinition.settings.agent = {};
      }
      appDefinition.settings.agent.agentId = publishedAgentId;
      console.log('[Preview] Injected voice agent ID:', publishedAgentId);
    }

    // Debug: log what we're sending to the player
    console.log('[Preview] App definition being sent:', appDefinition);
    console.log('[Preview] Screens:', appDefinition.screens.map(s => ({ name: s.name, widgets: s.widgets?.length })));
    console.log('[Preview] Settings:', appDefinition.settings.name);

    // Encode the app definition as base64 for URL transport
    const jsonString = JSON.stringify(appDefinition);
    const base64Data = btoa(encodeURIComponent(jsonString));

    // Open player in new tab with encoded data
    const playerBaseUrl = import.meta.env.VITE_PLAYER_URL || 'http://localhost:3003';
    const playerUrl = `${playerBaseUrl}/?data=${base64Data}`;
    console.log('[Preview] Opening player URL:', playerUrl.substring(0, 100) + '...');
    window.open(playerUrl, '_blank');
  }, [generateAppDefinition, publishedAgentId]);

  // Format relative time
  const formatRelativeTime = (isoString: string): string => {
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} min${diffMins > 1 ? 's' : ''} ago`;

    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;

    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-black/30"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Slide-out panel */}
      <div
        className="w-full max-w-md bg-white shadow-2xl flex flex-col animate-slide-in-right"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Publish Voice App</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none p-1"
          >
            x
          </button>
        </div>

        {/* Content - scrollable */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
          {/* Loading indicator */}
          {isLoadingStatus && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full" />
              <span className="ml-2 text-gray-500">Loading...</span>
            </div>
          )}

          {!isLoadingStatus && (
            <>
          {/* App Preview */}
          <div className="relative">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Preview
            </div>

            {/* Phone frame */}
            <div className="mx-auto w-48 h-80 bg-gray-900 rounded-3xl p-2 shadow-lg">
              <div className="w-full h-full bg-white rounded-2xl overflow-hidden flex flex-col">
                {/* Status bar mockup */}
                <div className="h-6 bg-gray-100 flex items-center justify-center">
                  <div className="w-16 h-1 bg-gray-300 rounded-full" />
                </div>

                {/* App content preview */}
                <div
                  className="flex-1 p-3"
                  style={{ backgroundColor: appSettings.theme.darkMode ? '#1a1a1a' : '#fff' }}
                >
                  {/* App title */}
                  <div
                    className="text-center mb-3"
                    style={{ color: appSettings.theme.darkMode ? '#fff' : '#000' }}
                  >
                    <div className="text-xs font-semibold">{appSettings.name}</div>
                  </div>

                  {/* Screens preview */}
                  <div className="space-y-2">
                    {connectedScreens.slice(0, 3).map((screen) => (
                      <div
                        key={screen.id}
                        className="px-2 py-1.5 rounded text-[8px] truncate"
                        style={{
                          backgroundColor: appSettings.theme.primaryColor + '20',
                          color: appSettings.theme.primaryColor,
                        }}
                      >
                        {screen.isHome && '🏠 '}
                        {screen.title}
                      </div>
                    ))}
                    {connectedScreens.length > 3 && (
                      <div className="text-center text-[8px] text-gray-400">
                        +{connectedScreens.length - 3} more
                      </div>
                    )}
                  </div>

                  {/* Voice indicator */}
                  <div className="absolute bottom-12 left-1/2 -translate-x-1/2">
                    <div
                      className="w-8 h-8 rounded-full flex items-center justify-center animate-pulse"
                      style={{ backgroundColor: appSettings.theme.primaryColor }}
                    >
                      <svg
                        className="w-4 h-4 text-white"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z"
                        />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* Home bar */}
                <div className="h-4 bg-gray-100 flex items-center justify-center">
                  <div className="w-8 h-0.5 bg-gray-400 rounded-full" />
                </div>
              </div>
            </div>

            {/* App info below preview */}
            <div className="mt-3 text-center">
              <div className="text-sm font-medium text-gray-700">{appSettings.name}</div>
              <div className="text-xs text-gray-500">
                {connectedScreens.length} screen{connectedScreens.length !== 1 ? 's' : ''}
              </div>
            </div>
          </div>

          {/* Hosting Section */}
          <div className="pt-4 border-t border-gray-100">
            <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
              Hosting
            </div>

            {/* Subdomain */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subdomain
              </label>
              <div className="flex items-center">
                <input
                  type="text"
                  value={subdomain}
                  onChange={(e) => {
                    setSubdomain(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''));
                    setError(null); // Clear error when user types
                  }}
                  placeholder="myapp"
                  className={`flex-1 px-3 py-2 border rounded-l-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white text-gray-900 ${
                    error ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-500 text-sm">
                  .theblinkin.app
                </span>
              </div>
              {error && (
                <p className="mt-1.5 text-sm text-red-600">{error}</p>
              )}
            </div>

            {/* Custom Domain */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Custom Domain (optional)
              </label>
              <input
                type="text"
                value={customDomain}
                onChange={(e) => setCustomDomain(e.target.value)}
                placeholder="app.example.com"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent bg-white text-gray-900"
              />
              {customDomain && (
                <p className="text-xs text-gray-400 mt-1">
                  Add CNAME: {customDomain} → apps.theblinkin.app
                </p>
              )}
            </div>

            {/* Allow Embedding */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={allowEmbedding}
                onChange={(e) => setAllowEmbedding(e.target.checked)}
                className="w-4 h-4 text-violet-600 rounded focus:ring-violet-500"
              />
              <span className="text-sm text-gray-700">Allow embedding (iframe)</span>
            </label>
          </div>

          {/* Status Section */}
          {publishStatus.isPublished && (
            <div className="pt-4 border-t border-gray-100">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-4">
                Status
              </div>

              <div className="flex items-center gap-2 mb-3">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                <span className="text-sm font-medium text-gray-700">Published</span>
                {publishStatus.lastPublishedAt && (
                  <span className="text-xs text-gray-400">
                    · Last update: {formatRelativeTime(publishStatus.lastPublishedAt)}
                  </span>
                )}
              </div>

              {/* Share buttons */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleCopyLink}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                  Copy Link
                </button>

                <button
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 text-sm rounded-lg hover:bg-gray-200 transition-colors"
                  title="Coming soon"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
                  </svg>
                  QR Code
                </button>
              </div>

              {publishStatus.subdomain && (
                <div className="mt-3 space-y-2">
                  {/* Local testing URL */}
                  <div>
                    <span className="text-xs text-gray-400 block mb-1">Test locally:</span>
                    <a
                      href={`${import.meta.env.VITE_PLAYER_URL || 'http://localhost:3003'}/?app=${publishStatus.subdomain}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-violet-600 hover:text-violet-700 underline"
                    >
                      localhost:3003/?app={publishStatus.subdomain}
                    </a>
                  </div>
                  {/* Production URL */}
                  {publishStatus.liveUrl && (
                    <div>
                      <span className="text-xs text-gray-400 block mb-1">Production:</span>
                      <span className="text-sm text-gray-500">
                        {publishStatus.liveUrl}
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
          {publishStatus.isPublished ? (
            <>
              <button
                onClick={handleUnpublish}
                className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-700 transition-colors"
              >
                Unpublish
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePublish}
                  disabled={isPublishing}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50"
                >
                  {isPublishing ? 'Updating...' : 'Update'}
                </button>
                <button
                  onClick={handlePreview}
                  className="px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
                >
                  Test Voice
                </button>
              </div>
            </>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-800 transition-colors"
              >
                Cancel
              </button>
              <div className="flex items-center gap-2">
                <button
                  onClick={handlePreview}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-violet-600 bg-violet-50 rounded-lg hover:bg-violet-100 transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Preview
                </button>
                <button
                  onClick={handlePublish}
                  disabled={isPublishing || !subdomain.trim()}
                  className="px-4 py-2 text-sm font-medium text-white bg-violet-600 rounded-lg hover:bg-violet-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isPublishing ? 'Publishing...' : 'Publish'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Animation styles */}
      <style>{`
        @keyframes slide-in-right {
          from {
            transform: translateX(100%);
          }
          to {
            transform: translateX(0);
          }
        }
        .animate-slide-in-right {
          animation: slide-in-right 0.2s ease-out;
        }
      `}</style>
    </div>
  );
}
